// ===== storage.ts - IndexedDB wrapper using idb =====
//
// Multi-tenant: all record stores (tracks, waypoints, folders, settings)
// are scoped by userId. Tiles, tileMeta, tileRecords and forecastCache
// remain shared (device-wide caches — forecast data is public and identical
// per user).
//
// Performance plan Task 5: tile BODIES no longer live here. The one stable
// body store is the Cache Storage cache `pps-tiles-v3` (see tileBodyStore.ts),
// shared with the service worker. The `tiles` object store below is the
// LEGACY body store: read-only fallback, drained lazily on read (copy once
// into the new store, delete only that migrated entry). `tileRecords` holds
// the small per-tile metadata needed for ownership, size, last use, and
// cleanup — never the body itself.

import { openDB, type IDBPDatabase } from 'idb';
import { getCurrentUserId } from '../stores/authStore';

export interface Track {
  id: string;
  name: string;
  points: Array<{ lat: number; lon: number; ele?: number; time?: number }>;
  distance: number;
  duration: number;
  startTime: number;
  endTime?: number;
  folderId?: string;
  updated?: number; // last local modification timestamp (for sync)
  userId?: string; // multi-tenant partition key
  /** Whether the track is visible on the map (per-item toggle). */
  visible?: boolean;
  /** Individual track color (hex string). */
  color?: string;
}

export interface Waypoint {
  id: string;
  name: string;
  notes: string;
  lat: number;
  lon: number;
  shape: 'circle' | 'triangle' | 'square' | 'star' | 'diamond';
  tags: string[];
  folderId?: string;
  photo?: string; // base64 data URL (legacy single photo)
  photos: string[]; // base64 data URLs (multiple photos)
  address?: string; // reverse-geocoded postal address (Nominatim)
  created: number;
  updated?: number; // last local modification timestamp (for sync conflict resolution)
  userId?: string; // multi-tenant partition key
  /** Whether the waypoint is visible on the map (per-item toggle). */
  visible?: boolean;
  /** Individual waypoint color (hex string). Absent = legacy default. */
  color?: string;
  /**
   * Map symbol: a line-icon key ('anchor') or 'emoji:<emoji>' — see
   * lib/waypointIcons. Absent = legacy shape+colour marker.
   */
  icon?: string;
}

export interface Folder {
  id: string;
  name: string;
  /** Parent folder id for nesting (undefined = root) */
  parentId?: string;
  created: number;
  userId?: string; // multi-tenant partition key
}

export interface DownloadMeta {
  /** Unique id for this download. */
  id: string;
  /** Human-readable name (auto-generated from bbox). */
  name: string;
  /** Bounding box of the downloaded region. */
  bbox: { west: number; south: number; east: number; north: number };
  /** Center of the area (for flyTo). */
  center: { lng: number; lat: number };
  /** Zoom levels downloaded. */
  minZoom: number;
  maxZoom: number;
  /** Number of tiles actually saved. */
  tileCount: number;
  /** Layer id used (e.g. 'satellite_esri', 'streets'). */
  layerId: string;
  /** Layer name for display. */
  layerName: string;
  /** Total estimated size in bytes. */
  estimatedBytes: number;
  /** When the download completed. */
  createdAt: number;
  /** Optional thumbnail as data URL (small PNG). */
  thumbnail?: string;
}

export interface Settings {
  key: string;
  value: unknown;
  userId?: string; // multi-tenant partition key
}

let dbInstance: IDBPDatabase | null = null;

export async function getDB(): Promise<IDBPDatabase> {
  if (dbInstance) return dbInstance;
  dbInstance = await openDB('jps', 7, {
    upgrade(db, oldVersion) {
      if (oldVersion < 1) {
        if (!db.objectStoreNames.contains('tiles')) {
          db.createObjectStore('tiles');
        }
        if (!db.objectStoreNames.contains('tracks')) {
          db.createObjectStore('tracks', { keyPath: 'id' });
        }
        if (!db.objectStoreNames.contains('waypoints')) {
          db.createObjectStore('waypoints', { keyPath: 'id' });
        }
        if (!db.objectStoreNames.contains('folders')) {
          db.createObjectStore('folders', { keyPath: 'id' });
        }
        if (!db.objectStoreNames.contains('settings')) {
          db.createObjectStore('settings', { keyPath: 'key' });
        }
      }
      if (oldVersion < 2) {
        if (!db.objectStoreNames.contains('tileMeta')) {
          db.createObjectStore('tileMeta', { keyPath: 'key' });
        }
      }
      if (oldVersion < 3) {
        if (!db.objectStoreNames.contains('sync_queue')) {
          db.createObjectStore('sync_queue', { keyPath: 'id' });
        }
        if (!db.objectStoreNames.contains('sync_conflicts')) {
          db.createObjectStore('sync_conflicts', { keyPath: 'id' });
        }
      }
      // v4: no structural changes — existing records get userId via default
      // in the filter helpers below. The migration is data-level (lazy).
      // v5: add downloads + downMetas stores for the download-area feature
      if (oldVersion < 5) {
        if (!db.objectStoreNames.contains('downloads')) {
          db.createObjectStore('downloads', { keyPath: 'id' });
        }
        if (!db.objectStoreNames.contains('downMetas')) {
          db.createObjectStore('downMetas', { keyPath: 'id' });
        }
      }
      // v6: add the device-wide forecastCache store (persistent forecast-cell
      // cache). NOT user-scoped — public forecast data is identical for every
      // signed-in user, so it must survive user switching. Never recreate or
      // clear existing stores.
      if (oldVersion < 6) {
        if (!db.objectStoreNames.contains('forecastCache')) {
          db.createObjectStore('forecastCache', { keyPath: 'key' });
        }
      }
      // v7: per-tile metadata for the stable tile body store (Task 5).
      // Small records only — key, size, last access, downloaded-region
      // owner ids. The tile body itself lives in Cache Storage
      // 'pps-tiles-v3' (tileBodyStore.ts), never here. The legacy 'tiles'
      // body store is intentionally NOT touched: it stays as a read-only
      // fallback that is drained lazily per entry on read.
      if (oldVersion < 7) {
        if (!db.objectStoreNames.contains('tileRecords')) {
          db.createObjectStore('tileRecords', { keyPath: 'key' });
        }
      }
    },
  });
  return dbInstance;
}

// ─── User-scoped helpers ───────────────────────────────────────────

/**
 * Get the current user's ID, or throw if not authenticated.
 * Call this in data operations that require a user.
 */
function requireUserId(): string {
  const uid = getCurrentUserId();
  if (!uid) throw new Error('Not authenticated — no user context for data operation');
  return uid;
}

/**
 * Prefix a settings key with the current user ID so each user's
 * preferences (layer states, draft recordings, sync timestamps) are isolated.
 */
function userKey(key: string): string {
  const uid = requireUserId();
  return `${uid}:${key}`;
}

// ─── Clear user-scoped data (for user switching) ───────────────────

/**
 * Delete ALL local data for a specific user — tracks, waypoints, folders,
 * and user-scoped settings. Tiles, tileRecords and tileMeta are NOT touched
 * (shared device caches — map tiles and public forecast data are identical
 * for every user, so they must survive user switching, and a signed-in
 * user's records never enter the shared tile stores).
 */
export async function clearUserData(userId: string): Promise<void> {
  const db = await getDB();

  // Delete tracks belonging to this user
  const allTracks = (await db.getAll('tracks')) as Track[];
  for (const t of allTracks) {
    if (t.userId === userId || !t.userId) await db.delete('tracks', t.id);
  }

  // Delete waypoints belonging to this user
  const allWaypoints = (await db.getAll('waypoints')) as Waypoint[];
  for (const w of allWaypoints) {
    if (w.userId === userId || !w.userId) await db.delete('waypoints', w.id);
  }

  // Delete folders belonging to this user
  const allFolders = (await db.getAll('folders')) as Folder[];
  for (const f of allFolders) {
    if (f.userId === userId || !f.userId) await db.delete('folders', f.id);
  }

  // Delete user-scoped settings (prefix match)
  const allSettings = await db.getAllKeys('settings');
  for (const k of allSettings) {
    const keyStr = String(k);
    if (keyStr.startsWith(`${userId}:`)) {
      await db.delete('settings', k);
    }
  }

  // Delete sync queue + conflicts for this user
  const queueItems = (await db.getAll('sync_queue')) as Array<{ id: string; userId?: string }>;
  for (const q of queueItems) {
    if (q.userId === userId || !q.userId) await db.delete('sync_queue', q.id);
  }
  const conflictItems = (await db.getAll('sync_conflicts')) as Array<{
    id: string;
    userId?: string;
  }>;
  for (const c of conflictItems) {
    if (c.userId === userId || !c.userId) await db.delete('sync_conflicts', c.id);
  }
}

// ===== Tracks =====
export async function saveTrack(track: Track): Promise<void> {
  const uid = requireUserId();
  const db = await getDB();
  await db.put('tracks', { ...track, userId: track.userId || uid });
}

export async function getTrack(id: string): Promise<Track | undefined> {
  const uid = requireUserId();
  const db = await getDB();
  const track = await db.get('tracks', id);
  if (!track) return undefined;
  // Filter: only return if it belongs to this user (or has no userId = legacy)
  if (track.userId !== uid && track.userId !== undefined) return undefined;
  return track;
}

export async function getAllTracks(): Promise<Track[]> {
  const uid = requireUserId();
  const db = await getDB();
  const all = (await db.getAll('tracks')) as Track[];
  return all.filter((t) => t.userId === uid || t.userId === undefined);
}

export async function deleteTrack(id: string): Promise<void> {
  const db = await getDB();
  await db.delete('tracks', id);
}

// ===== Waypoints =====
export async function saveWaypoint(wp: Waypoint): Promise<void> {
  const uid = requireUserId();
  const db = await getDB();
  await db.put('waypoints', { ...wp, userId: wp.userId || uid });
}

export async function getWaypoint(id: string): Promise<Waypoint | undefined> {
  const uid = requireUserId();
  const db = await getDB();
  const wp = await db.get('waypoints', id);
  if (!wp) return undefined;
  if (wp.userId !== uid && wp.userId !== undefined) return undefined;
  return wp;
}

export async function getAllWaypoints(): Promise<Waypoint[]> {
  const uid = requireUserId();
  const db = await getDB();
  const all = (await db.getAll('waypoints')) as Waypoint[];
  return all.filter((w) => w.userId === uid || w.userId === undefined);
}

export async function deleteWaypoint(id: string): Promise<void> {
  const db = await getDB();
  await db.delete('waypoints', id);
}

// ===== Folders =====
export async function saveFolder(folder: Folder): Promise<void> {
  const uid = requireUserId();
  const db = await getDB();
  await db.put('folders', { ...folder, userId: folder.userId || uid });
}

export async function getAllFolders(): Promise<Folder[]> {
  const uid = requireUserId();
  const db = await getDB();
  const all = (await db.getAll('folders')) as Folder[];
  return all.filter((f) => f.userId === uid || f.userId === undefined);
}

export async function deleteFolder(id: string): Promise<void> {
  const db = await getDB();
  await db.delete('folders', id);
}

// ===== Settings (user-scoped via key prefix) =====
export async function getSetting<T>(key: string, defaultValue: T): Promise<T> {
  const db = await getDB();
  const row = await db.get('settings', userKey(key));
  return row ? (row.value as T) : defaultValue;
}

export async function setSetting(key: string, value: unknown): Promise<void> {
  const uid = requireUserId();
  const db = await getDB();
  await db.put('settings', { key: userKey(key), value, userId: uid });
}

// ===== Tiles — LEGACY body store (read-only fallback) =====
//
// Performance plan Task 5: new tile bodies are written ONLY to the stable
// Cache Storage store 'pps-tiles-v3' (tileBodyStore.ts). The 'tiles' store
// below holds bodies written by app versions before the upgrade; it is read
// as a fallback and each entry is migrated once (copied into the new store,
// then deleted here). Nothing bulk-clears or recreates it during activation.
// These helpers exist for that migration path only — no caller writes here.

/** Read a legacy cached tile (old app body store). */
export async function getLegacyTile(key: string): Promise<ArrayBuffer | undefined> {
  const db = await getDB();
  const blob = await db.get('tiles', key);
  if (!blob) return undefined;
  return blob.arrayBuffer();
}

/** Delete exactly one migrated legacy tile body. */
export async function deleteLegacyTile(key: string): Promise<void> {
  const db = await getDB();
  await db.delete('tiles', key);
}

/** Count remaining unmigrated legacy bodies (diagnostics only). */
export async function getLegacyTileCount(): Promise<number> {
  const db = await getDB();
  return db.count('tiles');
}

/** Delete every legacy body (user-initiated full cache clear only). */
export async function clearLegacyTiles(): Promise<void> {
  const db = await getDB();
  await db.clear('tiles');
}

// ===== Tile records — per-tile metadata for the stable body store =====
//
// Small metadata records keyed by the SAME cache key as the body in
// 'pps-tiles-v3'. Never stores the body itself. Fields: byte size, last
// access (LRU cleanup), and downloaded-region owner ids (a tile can belong
// to several explicit downloads; owned tiles are never auto-evicted).

export interface TileRecord {
  key: string;
  /** Byte size of the stored body. */
  size: number;
  /** Last read/write time (ms epoch) — LRU cleanup order. */
  lastAccess: number;
  /** Ids of explicit downloaded regions that own this tile. */
  ownerIds: string[];
}

export async function saveTileRecord(record: TileRecord): Promise<void> {
  const db = await getDB();
  await db.put('tileRecords', record);
}

export async function getTileRecord(key: string): Promise<TileRecord | undefined> {
  const db = await getDB();
  return db.get('tileRecords', key);
}

export async function getAllTileRecords(): Promise<TileRecord[]> {
  const db = await getDB();
  return db.getAll('tileRecords');
}

export async function deleteTileRecord(key: string): Promise<void> {
  const db = await getDB();
  await db.delete('tileRecords', key);
}

export async function clearTileRecords(): Promise<void> {
  const db = await getDB();
  await db.clear('tileRecords');
}

/** Mark a tile record as touched now (LRU refresh). Creates it when the
 *  body is known to exist but no record does yet. */
export async function touchTileRecord(key: string, size: number): Promise<void> {
  const db = await getDB();
  const existing = await db.get('tileRecords', key);
  await db.put('tileRecords', {
    key,
    size,
    lastAccess: Date.now(),
    ownerIds: existing ? ((existing as TileRecord).ownerIds ?? []) : [],
  });
}

/** Record that an explicit downloaded region owns this tile. Merges into
 *  any existing ownership — overlapping downloads share the same body. */
export async function addTileOwnership(key: string, size: number, ownerId: string): Promise<void> {
  const db = await getDB();
  const existing = (await db.get('tileRecords', key)) as TileRecord | undefined;
  const ownerIds = existing?.ownerIds ?? [];
  if (!ownerIds.includes(ownerId)) ownerIds.push(ownerId);
  await db.put('tileRecords', {
    key,
    size: existing?.size ?? size,
    lastAccess: existing?.lastAccess ?? Date.now(),
    ownerIds,
  });
}

/** Remove one downloaded region's ownership from every tile it owns (called
 *  when the user deletes the region). Tiles that lose their last owner
 *  become ordinary live tiles again. */
export async function removeTileOwnership(ownerId: string): Promise<number> {
  const db = await getDB();
  const records = (await db.getAll('tileRecords')) as TileRecord[];
  let touched = 0;
  for (const record of records) {
    if (!record.ownerIds.includes(ownerId)) continue;
    const ownerIds = record.ownerIds.filter((id) => id !== ownerId);
    await db.put('tileRecords', { ...record, ownerIds });
    touched += 1;
  }
  return touched;
}

// ===== Tile Metadata — shared, NOT user-scoped =====

export interface TileCacheMeta {
  key: string;
  sourceUrl: string;
  tileCount: number;
  minZoom: number;
  maxZoom: number;
  createdAt: number;
}

export async function saveTileCacheMeta(meta: TileCacheMeta): Promise<void> {
  const db = await getDB();
  await db.put('tileMeta', meta);
}

export async function getTileCacheMetas(): Promise<TileCacheMeta[]> {
  const db = await getDB();
  return db.getAll('tileMeta');
}

export async function deleteTileCacheMeta(key: string): Promise<void> {
  const db = await getDB();
  await db.delete('tileMeta', key);
}

// ===== Storage Estimation =====

export interface StorageEstimate {
  usage: number;
  quota: number;
  tileCount: number;
}

/** Count bodies in the stable tile body store (Cache Storage 'pps-tiles-v3'). */
export async function getTileCount(): Promise<number> {
  const { countTileBodies } = await import('./tileBodyStore');
  return countTileBodies();
}

export async function getStorageEstimate(): Promise<StorageEstimate> {
  const tileCount = await getTileCount();
  let usage = 0;
  let quota = 0;
  try {
    if ('storage' in navigator && 'estimate' in navigator.storage) {
      const est = await navigator.storage.estimate();
      usage = est.usage ?? 0;
      quota = est.quota ?? 0;
    }
  } catch {
    // storage API unavailable
  }
  return { usage, quota, tileCount };
}

// ===== Download Metas — persisted download region metadata =====

export async function saveDownloadMeta(meta: DownloadMeta): Promise<void> {
  const db = await getDB();
  await db.put('downMetas', meta);
}

export async function getAllDownloadMetas(): Promise<DownloadMeta[]> {
  const db = await getDB();
  return db.getAll('downMetas');
}

export async function deleteDownloadMeta(id: string): Promise<void> {
  const db = await getDB();
  await db.delete('downMetas', id);
}
